<template>
  <b-modal
    id="chat-agent-detail-modal"
    ref="chatAgentDetailModal"
    :title="$t('title.chatAgentDetail')"
    size="lg"
    hide-footer
    content-class="shadow"
    no-close-on-backdrop
    @hide="onModalHide"
  >
    <div class="chat-agent-detail">
      <!-- Agent Names -->
      <b-card
        class="mb-1"
      >
        <b-row>
          <b-col
            v-if="englishName"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.titleEN') }}</label>
              <p class="mb-0">
                {{ englishName }}
              </p>
            </div>
          </b-col>
          <b-col  
            v-if="chineseName"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.titleZh') }}</label>
              <p class="mb-0">
                {{ chineseName }}
              </p>
            </div>
          </b-col>
          <b-col
            v-if="khmerName"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.titleKm') }}</label>
              <p class="mb-0">
                {{ khmerName }}
              </p>
            </div>
          </b-col>
          <b-col
            v-if="vietnameseName"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.titleVi') }}</label>
              <p class="mb-0">
                {{ vietnameseName }}
              </p>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- Agent Task -->
      <b-card class="mb-1">
        <!-- <h6>{{ $t('field.ability') }}</h6> -->
        <b-row>
          <b-col
            v-if="englishAbility"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.ability_des_en') }}</label>
              <p
                class="mb-0"
                v-html="englishAbility.replace(/\n/g, '<br/>')"
              />
            </div>
          </b-col>
          <b-col
            v-if="chineseAbility"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.ability_des_zh') }}</label>
              <p
                class="mb-0"
                v-html="chineseAbility.replace(/\n/g, '<br/>')"
              />
            </div>
          </b-col>
          <b-col
            v-if="khmerAbility"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.ability_des_km') }}</label>
              <p class="mb-0" v-html="khmerAbility.replace(/\n/g, '<br/>')"></p>
            </div>
          </b-col>
          <b-col
            v-if="vietnameseAbility"
            md="6"
            class="mb-2"
          >
            <div class="detail-group">
              <label>{{ $t('field.ability_des_vi') }}</label>
              <p class="mb-0" v-html="vietnameseAbility.replace(/\n/g, '<br/>')"></p>
            </div>
          </b-col>
          <!-- <b-col md="6" class="mb-1">
            <div class="detail-group">
              <label>{{ $t('field.abilityKm') }}</label>
              <p class="mb-0" v-html="agent.ability.km.replace(/\n/g, '<br/>')"></p>
            </div>
          </b-col>
          <b-col md="6" class="mb-1">
            <div class="detail-group">
              <label>{{ $t('field.abilityVi') }}</label>
              <p class="mb-0" v-html="agent.ability.vi.replace(/\n/g, '<br/>')"></p>
            </div>
          </b-col> -->
        </b-row>
      </b-card>

      <!-- Service System Section -->
      <b-card
        v-if="agent.isServiceSystemEnabled"
        class="mb-1"
      >
        <h6 class="mb-2">
          {{ $t('field.serviceSystem') }}
        </h6>

        <!-- Service System Info -->
        <div class="detail-group mb-2">
          <!-- Projects -->
          <div class="mb-2">
            <label>{{ $t('breadcrumb.project') }}</label>
            <div
              v-for="project in agent.projects"
              :key="project.id"
              class="mb-1"
            >
              {{ getProjectLabel(project) || '-' }}
            </div>
          </div>

          <!-- Service Category -->
          <div class="mb-2">
            <label>{{ $t('field.category') }}</label>
            <p class="mb-0">
              {{ getCategoryLabel(agent.serviceCategory) || '-' }}
            </p>
          </div>

          <!-- Service Type -->
          <div class="mb-2">
            <label>{{ $t('field.type') }}</label>
            <p class="mb-0">
              {{ getServiceTypeLabel(agent.serviceType) || '-' }}
            </p>
          </div>

          <!-- Service Sub Type -->
          <div class="mb-2">
            <label>{{ $t('field.subType') }}</label>
            <p class="mb-0">
              {{ getServiceSubTypeLabel(agent.serviceSubType) || '-' }}
            </p>
          </div>
        </div>
      </b-card>

      <!-- Steps Section -->
      <b-card>
        <h6 class="mb-2">
          {{ $t('field.workflowSteps') }}
        </h6>
        <div
          class="step-list"
          :style="stepListPaddingLeft"
        >
          <div
            v-for="(step, index) in agent.steps"
            :key="index"
            class="step-card position-relative mt-1"
          >
            <!-- Step Number Badge -->
            <div class="step-number">
              <span>{{ index + 1 }}</span>
            </div>

            <!-- Vertical Line -->
            <div
              v-if="index < (agent.steps || []).length - 1"
              class="step-connector"
            />

            <b-row>
              <b-col>
                <h6 class="text-primary">
                  {{ step.name }}
                </h6>
                <p class="mb-1">
                  {{ step.description }}
                </p>

                <!-- Question Type Display -->
                <div class="detail-group mb-2">
                  <label>{{ $t('field.questionType') }}</label>
                  <p class="mb-1">
                    <b-badge variant="info">
                      {{ getQuestionTypeLabel(step.questionType) }}
                    </b-badge>
                  </p>
                </div>

                <!-- Answers Display for Multiple Choice -->
                <template v-if="step.questionType !== QUESTION_TYPE.OPEN && step.answers && (step.answers || []).length > 0 && step.questionType !== QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT && step.questionType !== QUESTION_TYPE.ATTACHMENTS">
                  <div class="detail-group mb-2">
                    <label>{{ $t('field.answers') }}</label>
                    <div class="answers-list">
                      <div
                        v-for="(answer, index) in step.answers"
                        :key="index"
                        class="answer-item"
                      >
                        <div class="d-flex align-items-start justify-content-center">
                          <div class="answer-number">
                            {{ index + 1 }}
                          </div>
                          <div class="answer-content">
                            <template v-if="answer.en">
                              <div class="answer-text">
                                English: {{ answer.en }}
                              </div>
                            </template>
                            <template v-if="answer.zh">
                              <div class="answer-text ">
                                中文: {{ answer.zh }}
                              </div>
                            </template>
                            <template v-if="answer.km">
                              <div class="answer-text">
                                ខ្មែរ: {{ answer.km }}
                              </div>
                            </template>
                            <template v-if="answer.vi">
                              <div class="answer-text">
                                Tiếng Việt: {{ answer.vi }}
                              </div>
                            </template>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-if="step.parameters && step.parameters.length && step.questionType !== QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT">
                  <div class="detail-group mb-2">
                    <label>{{ $t('field.parameters') }}</label>
                    <div class="d-flex flex-wrap gap-1">
                      <b-badge
                        v-for="(parameter, paramIndex) in step.parameters"
                        :key="paramIndex"
                        variant="primary"
                        class="mr-1"
                      >
                        {{ getParameterName(parameter) }} ({{ parameter.key }})
                      </b-badge>
                    </div>
                  </div>
                </template>

                <div class="detail-group">
                  <label>{{ $t('field.callbackUrl') }}</label>
                  <b-link>{{ step.callbackUrl }}</b-link>
                </div>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-card>

      <!-- Modal Footer -->
      <div class="text-right mt-3">
        <b-button
          variant="primary"
          @click="closeModal"
        >
          {{ $t('button.close') }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BCard,
  BRow,
  BCol,
  BButton,
  BBadge,
  BLink,
} from 'bootstrap-vue'

export default {
  name: 'ChatAgentDetailModal',
  components: {
    BModal,
    BCard,
    BRow,
    BCol,
    BButton,
    BBadge,
    BLink,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
    agentData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      agent: {
        id: null,
        name: {
          en: '',
          zh: '',
          vi: '',
          km: '',
        },
        ability: {
          en: '',
          zh: '',
          vi: '',
          km: '',
        },
        steps: [],
        isServiceSystemEnabled: false,
        projects: [],
        serviceCategory: null,
        serviceType: null,
      },
      QUESTION_TYPE: {
        OPEN: 1,
        MULTI_CHOICE_SINGLE_ANSWER: 2,
        MULTI_CHOICE_MULTI_ANSWER: 3,
        MULTI_CHOICE_FOR_PRODUCT: 6,
        ATTACHMENTS: 7,
      },
    }
  },
  computed: {
    stepListPaddingLeft() {
      return {
        paddingLeft: (this.agent.steps || []).length > 0 ? '40px' : '0',
      }
    },
    locale() {
      return this.$i18n.locale
    },
    localeForTranslation() {
      return this.$i18n.locale.split('-')[0];
    },
    // Name
    englishName() {
      return this.agent?.name?.en;
    },
    chineseName() {
      return this.agent?.name?.zh;
    },
    khmerName() {
      return this.agent?.name?.km;
    },
    vietnameseName() {
      return this.agent?.name?.vi;
    },
    englishAbility() {
      return this.agent?.ability?.en;
    },
    chineseAbility() {
      return this.agent?.ability?.zh;
    },
    khmerAbility() {
      return this.agent?.ability?.km;
    },
    vietnameseAbility() {
      return this.agent?.ability?.vi;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.showModal()
      }
    },
    agentData: {
      handler(newVal) {
        if (newVal) {
          this.initializeData()
        }
      },
      deep: true,
    },
  },
  methods: {
    getParameterName(parameter) {
      return parameter?.nameTranslate?.[this.localeForTranslation] || parameter?.nameTranslate?.en || parameter?.nameTranslate?.zh || parameter?.nameTranslate?.km || parameter?.nameTranslate?.vi || parameter?.name || '-';
    },
    getProjectLabel(project) {
      if (project?.projectId === 'ALL_PROJECT') return this.$t('general.all');
      const name = project?.translation?.name || project?.name;
      if (typeof name === 'string') return name;
      return name?.[this.localeForTranslation] || name?.en || name?.zh || name?.km || name?.vi || '-';
    },
    getCategoryLabel(category) {
      if (!category && !category?.name) return '-';
      return category?.name?.[this.localeForTranslation] || category?.name?.en || category?.name?.zh || category?.name?.km || category?.name?.vi || '-';
    },
    getServiceTypeLabel(serviceType) {
      if (!serviceType && !serviceType?.name) return '-';
      return serviceType?.name?.[this.localeForTranslation] || serviceType?.name?.en || serviceType?.name?.zh || serviceType?.name?.km || serviceType?.name?.vi || '-';
    },
    getServiceSubTypeLabel(serviceSubType) {
      if (!serviceSubType && !serviceSubType?.name) return '-';
      return serviceSubType?.name?.[this.localeForTranslation] || serviceSubType?.name?.en || serviceSubType?.name?.zh || serviceSubType?.name?.km || serviceSubType?.name?.vi || '-';
    },
    initializeData() {
      this.agent = {
        ...this.agentData,
        steps: this.agentData.steps?.map(step => ({
          ...step,
          answers: step.answers?.map(answer => ({
            en: answer.answer?.en || '',
            zh: answer.answer?.zh || '',
            km: answer.answer?.km || '',
            vi: answer.answer?.vi || '',
          })) || [],
        })),
        projects: this.agentData.projects || [],
        serviceCategory: this.agentData.serviceCategory || null,
        serviceType: this.agentData.serviceType || null,
      }
    },
    showModal() {
      this.$refs.chatAgentDetailModal.show()
    },
    closeModal() {
      this.$refs.chatAgentDetailModal.hide()
    },
    onModalHide() {
      this.$emit('hide-modal')
    },
    getParamName(param, stepData) {
      return stepData.flatMap(step => step.parameters).find(p => p.key === param)?.name
    },
    getQuestionTypeLabel(type) {
      switch (type) {
        case this.QUESTION_TYPE.OPEN:
          return this.$t('field.openQuestion')
        case this.QUESTION_TYPE.MULTI_CHOICE_SINGLE_ANSWER:
          return this.$t('field.singleChoice')
        case this.QUESTION_TYPE.MULTI_CHOICE_MULTI_ANSWER:
          return this.$t('field.multipleChoice')
        case this.QUESTION_TYPE.MULTI_CHOICE_FOR_PRODUCT:
          return this.$t('field.multipleChoiceForProduct')
        case this.QUESTION_TYPE.ATTACHMENTS:
          return this.$t('field.attachments')
        default:
          return this.$t('field.openQuestion')
      }
    },
    getNameByLang(data) {
      if (!data?.name) return ''

      const locale = this.$i18n.locale.split('-')[0]
      return data?.name?.[locale] || data?.name?.en || data?.name?.zh || data?.name?.km || data?.name?.vi || '-'
    },
  },
}
</script>

<style lang="scss" scoped>
  .chat-agent-detail {
    .section-title {
      color: #5e5873;
      font-weight: 600;
      font-size: 1rem;
    }

    .detail-group {
      label {
        font-weight: 600;
        color: #5e5873;
        font-size: 0.9rem;
        display: block;
        margin-bottom: 0.25rem;
      }
    }

    .step-list {
      position: relative;
    }

    .step-card {
      background-color: white;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      padding: 0.75rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
      margin-bottom: 0.75rem;
    }

    .step-number {
      position: absolute;
      left: -40px;
      top: 15px;
      width: 30px;
      height: 30px;
      background-color: #8d5185;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      z-index: 2;
    }

    .step-connector {
      position: absolute;
      left: -25px;
      top: 30px;
      width: 2px;
      height: calc(100% + 20px);
      background-color: #dee2e6;
      z-index: 1;
    }

    .badge {
      padding: 0.35rem 0.6rem;
      font-size: 0.85rem;
    }

    h6 {
      font-size: 0.95rem;
      margin-bottom: 0.5rem;
    }

    .mb-1 {
      margin-bottom: 0.5rem !important;
    }

    .mb-2 {
      margin-bottom: 0.75rem !important;
    }

    .mb-3 {
      margin-bottom: 1rem !important;
    }

    .answers-list {
      background-color: #f8f8f8;
      border-radius: 0.5rem;
      padding: 0.75rem 1rem;

      .answer-item {
        padding: 0.5rem 0;
        border-bottom: 1px solid #ebe9f1;

        &:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        &:first-child {
          padding-top: 0;
        }
      }

      .answer-number {
        width: 24px;
        height: 24px;
        background: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
        color: #5e5873;
        font-weight: 600;
        margin-right: 0.75rem;
        flex-shrink: 0;
      }

      .answer-content {
        flex: 1;
      }

      .answer-text {
        font-size: 0.9rem;
        line-height: 1.4;
        color: #5e5873;
        padding-top: 0.25rem;

        &.text-secondary {
          color: #5e5873;
          font-size: 0.9rem;
          line-height: 1.4;
          padding-top: 0.25rem;
        }
      }
    }
  }
</style>
